// The ANDON LOOP — in-memory state machine for ship #1.
//
// Implements the lifecycle from the canonical Agicore doc, simplified
// for a single-tier demo:
//
//   andon event ↘
//                proposal → TIER_VERIFIED → TESTED → DEPLOYED
//                            ↓                ↓         ↑
//                       (rejected)       (rejected)  (success)
//
// Every transition appends to mutation_ledger; every ledger entry is
// SHA-256 hash-chained to the previous one (per ANDON_LOOP.md Phase 7).
// State lives in this module's closure — ship #2 will swap it for
// SQLite + a zustand subscriber, but the API stays the same.
//
// Approval gates, NBVE shadow windows, and ordered-N-of-N signers are
// out of scope for ship #1. The single tier is rule_add with
// AUTO_DEPLOY=true; sandbox failure is the only path to rejection.

import { generate, sandboxTest } from "./generator";
import {
  currentLengthTarget,
  findLengthRule,
  formatPredicate,
  MAX_LENGTH,
  type Rule,
  starterRules,
} from "./dsl";

// ─── Types ──────────────────────────────────────────────────────────────────

export type AndonTrigger = "guess_succeeded" | "manual_pull";

export interface AndonEvent {
  id: string;
  trigger: AndonTrigger;
  ts: number;
  /** Free-form context the responder reads when proposing a mutation. */
  payload: Record<string, unknown>;
}

export type ProposalStatus =
  | "proposed"
  | "tier_verified"
  | "tier_rejected"
  | "tested"
  | "rejected_by_sandbox"
  | "deployed";

export interface MutationProposal {
  id: string;
  andonEventId: string;
  /** Two tiers:
   *  - "rule_add"     — append a brand-new constraint to the ruleset.
   *  - "rule_ratchet" — raise the single LENGTH target in place (length
   *    only ever grows). Inferred automatically: a LENGTH proposal against
   *    the existing LENGTH rule is a ratchet; everything else is an add. */
  claimedTier: "rule_add" | "rule_ratchet";
  /** The rule the responder proposes. For an add, the new constraint; for
   *  a ratchet, the replacement LENGTH rule. */
  newRule: Rule;
  status: ProposalStatus;
  ts: number;
  /** Set when sandbox fails — explains why the proposal was rejected. */
  rejectionReason: string | null;
  /** Set when sandbox passes — a sample password the proposed ruleset
   *  produces. Surfaced in the ledger so reviewers can see what the
   *  AI's change actually yields. */
  sandboxSample: string | null;
}

/** What kinds of things land in the ledger. Drives the UI's icon /
 *  color choice per row in ship #2. */
export type LedgerKind =
  | "andon_pulled"
  | "proposed"
  | "tier_verified"
  | "tier_rejected"
  | "tested"
  | "rejected_by_sandbox"
  | "deployed";

export interface LedgerEntry {
  /** Sequence number — first entry is 1. */
  seq: number;
  ts: number;
  kind: LedgerKind;
  /** Anything that explains the transition. Stored opaque; the UI
   *  inspects shape per kind to render. */
  payload: Record<string, unknown>;
  /** Hex SHA-256 of previous entry's hash + canonical payload. The
   *  genesis entry uses "0".repeat(64) as the previous hash. */
  prevHash: string;
  hash: string;
}

export interface State {
  rules: Rule[];
  currentPassword: string;
  /** Append-only history of guesses. */
  guesses: Array<{ guess: string; ts: number; passed: boolean }>;
  events: AndonEvent[];
  proposals: MutationProposal[];
  ledger: LedgerEntry[];
}

// ─── Singleton state + subscribers ──────────────────────────────────────────

const GENESIS_HASH = "0".repeat(64);

const state: State = bootstrap();

// Snapshot returned by getState(). Rebuilt on every notify() so its
// identity changes — required by React's useSyncExternalStore, which
// uses Object.is to decide whether to re-render. Without this the
// loop mutates state correctly but the UI never updates.
let snapshot: Readonly<State> = { ...state };

const subscribers = new Set<(s: State) => void>();

function notify(): void {
  snapshot = { ...state };
  for (const sub of subscribers) {
    try { sub(snapshot); } catch (e) { console.warn("[andon-loop] subscriber threw:", e); }
  }
}

export function subscribe(fn: (s: State) => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** Read-only snapshot. The console uses this; UI in ship #2 reads
 *  via the subscribe-based binding instead. */
export function getState(): Readonly<State> {
  return snapshot;
}

function bootstrap(): State {
  const rules = starterRules();
  const pw = generate(rules);
  if (!pw) throw new Error("starter ruleset failed to generate — DSL is broken");
  return {
    rules,
    currentPassword: pw,
    guesses: [],
    events: [],
    proposals: [],
    ledger: [],
  };
}

// ─── Hash chaining ──────────────────────────────────────────────────────────

const encoder = new TextEncoder();

/** Hex-encoded SHA-256 of the input string. Uses Web Crypto; this
 *  module is browser-only by design. */
async function sha256Hex(input: string): Promise<string> {
  const bytes = await crypto.subtle.digest("SHA-256", encoder.encode(input));
  return Array.from(new Uint8Array(bytes))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function canonicalize(payload: Record<string, unknown>): string {
  // Stable key ordering so the hash is reproducible across runs even
  // when the original object had arbitrary insertion order.
  const sortedKeys = Object.keys(payload).sort();
  return JSON.stringify(payload, sortedKeys);
}

async function appendLedger(kind: LedgerKind, payload: Record<string, unknown>): Promise<LedgerEntry> {
  const prev = state.ledger[state.ledger.length - 1];
  const prevHash = prev ? prev.hash : GENESIS_HASH;
  const seq = state.ledger.length + 1;
  const ts = Date.now();
  const hash = await sha256Hex(`${prevHash}|${kind}|${ts}|${canonicalize(payload)}`);
  const entry: LedgerEntry = { seq, ts, kind, payload, prevHash, hash };
  state.ledger.push(entry);
  notify();
  return entry;
}

// ─── Responder contract ─────────────────────────────────────────────────────
//
// The thing that turns an andon event into a proposed mutation. Ship #1
// has a deterministic stub responder; ship #3 swaps in BabyAI. The
// signature stays the same — both produce a Rule, the lifecycle
// machinery doesn't care which.

export type Responder = (current: Readonly<State>, trigger: AndonEvent) => Promise<Rule>;

let responder: Responder = async () => {
  throw new Error("no responder registered — call setResponder(...) first");
};

export function setResponder(fn: Responder): void {
  responder = fn;
}

// ─── ID helpers ─────────────────────────────────────────────────────────────

function genId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Public actions ─────────────────────────────────────────────────────────

/** Submit a guess. Returns whether it matched. On match, pulls the
 *  andon cord and runs the full proposal lifecycle. */
export async function submitGuess(guess: string): Promise<boolean> {
  const passed = guess === state.currentPassword;
  state.guesses.push({ guess, ts: Date.now(), passed });
  notify();
  if (passed) await pullAndon("guess_succeeded", { winningGuess: guess });
  return passed;
}

/** Direct cord pull (the "cheat" button submits the password — same
 *  effect as a real guess match). Surfaced as its own entry point so
 *  the UI can wire a manual button later. */
export async function pullAndon(
  trigger: AndonTrigger,
  payload: Record<string, unknown> = {},
): Promise<void> {
  const event: AndonEvent = { id: genId("ae"), trigger, ts: Date.now(), payload };
  state.events.push(event);
  await appendLedger("andon_pulled", { eventId: event.id, trigger, payload });

  await runProposalLifecycle(event);
}

/** Submit the current password verbatim — the [CHEAT] button's action.
 *  Same effect as a correct guess but tagged differently in the ledger. */
export async function cheat(): Promise<void> {
  const current = state.currentPassword;
  state.guesses.push({ guess: current, ts: Date.now(), passed: true });
  notify();
  await pullAndon("guess_succeeded", { winningGuess: current, viaCheat: true });
}

// ─── Lifecycle ──────────────────────────────────────────────────────────────

async function runProposalLifecycle(event: AndonEvent): Promise<void> {
  // 1. Responder proposes a rule.
  const newRule = await responder(state, event);

  // Classify the mutation. A LENGTH proposal against the existing LENGTH
  // rule is a RATCHET — we replace the length target in place rather than
  // appending a contradictory second LENGTH rule. Everything else is a
  // plain rule_add. (This is what unfreezes length: the append-only model
  // could never grow it, because the equality pin always won.)
  const lengthRule = findLengthRule(state.rules);
  const isRatchet = newRule.predicate.kind === "LENGTH" && !!lengthRule;
  const claimedTier: MutationProposal["claimedTier"] = isRatchet ? "rule_ratchet" : "rule_add";

  // For a ratchet, resolve the from/to lengths up front so every ledger
  // entry can speak in terms of "LENGTH 3 → 5".
  const ratchetFrom = isRatchet ? currentLengthTarget(state.rules) : 0;
  const ratchetTo =
    isRatchet && newRule.predicate.kind === "LENGTH"
      ? (newRule.predicate.op === ">" ? newRule.predicate.n + 1 : newRule.predicate.n)
      : 0;

  const predicateText = isRatchet
    ? `LENGTH ${ratchetFrom} → ${ratchetTo}`
    : formatPredicate(newRule.predicate);
  const ruleLabel = isRatchet ? "length" : newRule.name;

  const proposal: MutationProposal = {
    id: genId("mp"),
    andonEventId: event.id,
    claimedTier,
    newRule,
    status: "proposed",
    ts: Date.now(),
    rejectionReason: null,
    sandboxSample: null,
  };
  state.proposals.push(proposal);
  await appendLedger("proposed", {
    proposalId: proposal.id,
    tier: claimedTier,
    ruleName: ruleLabel,
    predicate: predicateText,
  });

  // 2. Tier verification. rule_add is always valid in v0. A ratchet must
  //    actually INCREASE length and stay under the cap — those are the two
  //    ways a ratchet earns a real "tier rejected" andon event.
  if (isRatchet) {
    if (ratchetTo <= ratchetFrom) {
      proposal.status = "tier_rejected";
      proposal.rejectionReason = `ratchet must increase length — current ${ratchetFrom}, proposed ${ratchetTo}`;
      notify();
      await appendLedger("tier_rejected", { proposalId: proposal.id, reason: proposal.rejectionReason });
      return;
    }
    if (ratchetTo > MAX_LENGTH) {
      proposal.status = "tier_rejected";
      proposal.rejectionReason = `length cap reached — ${MAX_LENGTH} is the maximum, proposed ${ratchetTo}`;
      notify();
      await appendLedger("tier_rejected", { proposalId: proposal.id, reason: proposal.rejectionReason });
      return;
    }
  }
  proposal.status = "tier_verified";
  notify();
  await appendLedger("tier_verified", { proposalId: proposal.id });

  // 3. Sandbox: can the proposed ruleset still produce a password? For a
  //    ratchet we swap the LENGTH rule in place; for an add we append.
  const proposedRules: Rule[] =
    isRatchet && lengthRule
      ? state.rules.map((r) =>
          r.id === lengthRule.id
            ? { ...lengthRule, predicate: { kind: "LENGTH", op: "=", n: ratchetTo } }
            : r,
        )
      : [...state.rules, newRule];
  const sandbox = sandboxTest(proposedRules);
  if (!sandbox.passed) {
    proposal.status = "rejected_by_sandbox";
    proposal.rejectionReason = `sandbox exhausted ${sandbox.attemptsUsed} attempts without a passing candidate — proposed rule likely contradicts an existing one`;
    notify();
    await appendLedger("rejected_by_sandbox", {
      proposalId: proposal.id,
      attemptsUsed: sandbox.attemptsUsed,
      reason: proposal.rejectionReason,
    });
    return;
  }
  proposal.status = "tested";
  proposal.sandboxSample = sandbox.sample;
  notify();
  await appendLedger("tested", {
    proposalId: proposal.id,
    sandboxAttempts: sandbox.attemptsUsed,
    sampleUnderProposed: sandbox.sample,
  });

  // 4. Deploy — single tier with AUTO_DEPLOY=true.
  state.rules = proposedRules;
  const newPw = generate(state.rules);
  if (newPw) state.currentPassword = newPw;
  proposal.status = "deployed";
  notify();
  await appendLedger("deployed", {
    proposalId: proposal.id,
    tier: claimedTier,
    ruleName: ruleLabel,
    predicate: predicateText,
    ruleCountAfter: state.rules.length,
    lengthAfter: currentLengthTarget(state.rules),
  });
}

// ─── Reset (for the demo / tests) ───────────────────────────────────────────

export function reset(): void {
  state.rules = starterRules();
  const pw = generate(state.rules);
  state.currentPassword = pw ?? "";
  state.guesses = [];
  state.events = [];
  state.proposals = [];
  state.ledger = [];
  notify();
}
